
import Vue from 'vue';
import { db } from '@/firebase';
import { mapActions, mapState } from 'vuex';
import { uploadImageToRunnit, uploadVideoToRunnit } from '@/utils/runnitUpload';
import { hasChipRenderer } from '@/views/Runnits/RunnitSettings/PromptDrivenForm/ChipFields/utils';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _debounce from 'lodash/debounce';
import _isNil from 'lodash/isNil';
import _keys from 'lodash/keys';
import RunnitDynamicField from '@/views/Runnits/RunnitSettings/RunnitDynamicField.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import {
	RunnitNode,
	RunnitNodeField,
	RunnitNodeFieldGroup,
	RunnitNodeRun,
	RunnitNodeRunInputValue,
	RunnitNodeStaticFields,
	RunnitNodeStaticFieldsKey,
	RunnitNodeDef,
	RUNNIT_NODE_RUN_MODE,
	RUNNIT_NODE_RUN_STATE,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RUNNIT_NODE_DEF_TAG_TYPE,
	RUNNIT_NODE_FIELD_TYPE,
	extractPromptStringValue,
	PROMPT_MAGIC_MODE,
	RunnitNodeFieldType,
	RunnitUpload,
	RunnitNodeRunResult,
	FieldDefUuid,
	RunnitNodeFieldDisplayWIDTH_HEIGHT,
	RunnitNodeFieldDisplaySINGLE_SELECT,
	RunnitNodeFieldSelectOption,
	RunnitNodeFieldImageSelectOption,
	RunnitNodeFieldDisplaySLIDER,
	RunnitNodeFieldDisplayCFG,
	RunnitNodeFieldDisplaySTEPS,
	RunnitNodeFieldDisplayUPSCALE_FACTOR_SLIDER,
} from '@run-diffusion/shared';
import { validateWidthHeightValue } from '@/views/Runnits/RunnitSettings/WidthHeightField/utils';
import { validateSingleSelectValue, getSingleSelectOptions } from '@/views/Runnits/RunnitSettings/SingleSelect/utils';
import { validateSliderValue } from '@/views/Runnits/RunnitSettings/Sliders/utils';
import { mapFirebaseTimestampToDate } from '@/utils';
import { ModelCRUDMixin } from "@/mixins";
import { RunnitRemixMixin } from '@/mixins/RunnitRemixMixin';
import _trim from 'lodash/trim';
import { SNACKBAR_STATUS } from '@/constants/constants';
import _isNumber from 'lodash/isNumber';

/** First prompt, image, and video fields in form order (for paste targeting). */
interface FirstFieldsForPaste {
	firstImageField: RunnitNodeField | null;
	firstVideoField: RunnitNodeField | null;
}

/** Value shape for IMGS (multi-image) field when building uploads array. */
interface MultiImageInputValue {
	uploads?: RunnitUpload[];
	nodeRunResults?: RunnitNodeRunResult[];
}

export default Vue.extend({
	name: 'AutoSaveRunnitNodeRunContainer',
	props: {
		node: { type: Object, required: true },
		inputValues: { type: Object, required: true },
		staticInputValues: { type: Object, required: true }, // These are controlled externally from this component
		incrementAutoSaveTrigger: { type: Number, default: 0 },
		allowSave: { type: Boolean, default: true },
	},
	mixins: [
		ModelCRUDMixin,
		RunnitRemixMixin,
	],
	data () {
		return {
			debouncedDoSaveDraft: () => { },
			debouncedClearQueryParamsInputValues: () => { },
			debouncedApplyQueryParams: () => { },
			debounceCreated: false,
			hasChangesToSave: false,
			isSavingChanges: false,
			isLoadingQueryParams: false,
			inputValuesQueryParam: null,
			promptQueryParam: null,
			nodeRunIdQueryParam: null,
			nodeRunInputValues: null,
			nodeRunStaticInputValues: null,
			lastHealedDraftId: null as string | null,
			hasShownHealSnackbar: false,
			isPasteUploading: false,
			pasteUploadingCount: 0 as number,
		};
	},
	computed: {
		...mapState([
			'user',
			'draftRunnitNodeRun',
			'boundPropsDraftRunnitNodeRun',
			'loadingDraftRunnitNodeRun',
			'runnitState',
			'modelsState',
			'runnitNodesMap',
			'runnitNodes',
			'publicRunnitNodeDefTagsMap',
		]),
		shouldRenderFields () {
			return !(this.loadingDraftRunnitNodeRun || this.loadingDraftRunnitNodeRun === null);
		},
		isNodeRunQueuing () {
			return !!(
				this.draftRunnitNodeRun &&
				this.node.id === this.draftRunnitNodeRun.nodeId &&
				this.runnitState.isQueuingDraftRunnitNodeRun
			);
		},
		determinedFields () {
			return this.determineFields(this.node);
		},
	},
	watch: {
		node: {
			immediate: true,
			handler (newVal: RunnitNode, oldVal: RunnitNode) {
				const newId: string = this._get(newVal, 'id') || null;
				const oldId: string = this._get(oldVal, 'id') || null;
				if (newId !== oldId) {
					this.keepDebounceFunctionInFlight();
					// Reset heal tracking when node changes
					this.lastHealedDraftId = null;
					this.hasShownHealSnackbar = false;
				}
				if (newId && newId !== oldId) {
					// If query params exist, use applyQueryParams to ensure they take precedence
					// Otherwise, just apply defaults from draft
					if (this.$route.query.inputValues || this.$route.query.prompt || this.$route.query.nodeRunId) {
						// Update query param data properties from route if they were cleared
						if (!this.inputValuesQueryParam && this.$route.query.inputValues) {
							this.inputValuesQueryParam = this.$route.query.inputValues;
						}
						if (!this.promptQueryParam && this.$route.query.prompt) {
							this.promptQueryParam = this.$route.query.prompt;
						}
						if (!this.nodeRunIdQueryParam && this.$route.query.nodeRunId) {
							this.nodeRunIdQueryParam = this.$route.query.nodeRunId;
						}
						// Apply query params (which will take precedence over draft)
						this.debouncedApplyQueryParams();
					} else {
						// No query params, just apply defaults from draft
						this.emitNodeRunDefaults(this.draftRunnitNodeRun, newVal);
					}
				}
			},
		},
		draftRunnitNodeRun: {
			immediate: true,
			handler (newVal: RunnitNodeRun, oldVal: RunnitNodeRun) {
				const newId: string = this._get(newVal, 'id') || null;
				const oldId: string = this._get(oldVal, 'id') || null;
				if (newId !== oldId) {
					this.keepDebounceFunctionInFlight();
				}
				// Remove "newId &&" so that we also initialize when there is no draft (null)
				if (newId !== oldId) {
					// If query params exist, use applyQueryParams to ensure they take precedence
					// Otherwise, just apply defaults from draft
					if (this.$route.query.inputValues || this.$route.query.prompt || this.$route.query.nodeRunId) {
						// Update query param data properties from route if they were cleared
						if (!this.inputValuesQueryParam && this.$route.query.inputValues) {
							this.inputValuesQueryParam = this.$route.query.inputValues;
						}
						if (!this.promptQueryParam && this.$route.query.prompt) {
							this.promptQueryParam = this.$route.query.prompt;
						}
						if (!this.nodeRunIdQueryParam && this.$route.query.nodeRunId) {
							this.nodeRunIdQueryParam = this.$route.query.nodeRunId;
						}
						// Apply query params (which will take precedence over draft)
						this.debouncedApplyQueryParams();
					} else {
						// No query params, just apply defaults from draft
						this.emitNodeRunDefaults(newVal, this.node);
					}
				}
			},
		},
		incrementAutoSaveTrigger: {
			immediate: false,
			handler (newVal: number, oldVal: number) {
				if (newVal !== oldVal) {
					this.beginAutoSave(this.inputValues, this.staticInputValues);
				}
			},
		},
		'$route.query': {
			immediate: true,
			deep: true,
			handler () {
				// Update query param values from route
				this.inputValuesQueryParam = this.$route.query.inputValues || null;
				this.promptQueryParam = this.$route.query.prompt || null;
				this.nodeRunIdQueryParam = this.$route.query.nodeRunId || null;

				// Trigger debounced application of query params
				// This ensures all params are processed together with correct precedence
				this.debouncedApplyQueryParams();
			}
		},
		shouldRenderFields: {
			immediate: true,
			handler (newVal) {
				if (newVal) {
					// Once the draft loading state is resolved (either loaded or confirmed missing),
					// we can safely apply query params or initialize with defaults.
					if (this.$route.query.inputValues || this.$route.query.prompt || this.$route.query.nodeRunId) {
						this.debouncedApplyQueryParams();
					} else {
						this.emitNodeRunDefaults(this.draftRunnitNodeRun, this.node);
					}
				}
			},
		},
		determinedFields: {
			immediate: true,
			handler (newVal: (RunnitNodeFieldGroup | RunnitNodeField)[], oldVal: (RunnitNodeFieldGroup | RunnitNodeField)[]) {
				if (newVal !== oldVal) {
					this.buildFieldsByTypeMap(newVal);
					this.initPromptDrivenFormFieldGrouping();
				}
			}
		}
	},
	created () {
		this.initDebouncedMethods();
		// Initialize query param values from route
		this.inputValuesQueryParam = this.$route.query.inputValues || null;
		this.promptQueryParam = this.$route.query.prompt || null;
		this.nodeRunIdQueryParam = this.$route.query.nodeRunId || null;
		// Apply query params after initialization
		this.debouncedApplyQueryParams();
		this.initModelDraft();
	},
	destroyed () {
		this.updateModelState({
			modelDraft: null,
		});
	},
	methods: {
		...mapActions([
			'updateModelState',
			'updateRunnitState',
			'incrementPromptSelectedModelChangeTrigger',
			'updateSnackbar',
		]),
		initDebouncedMethods () {
			if (!this.debounceCreated) {
				this.debounceCreated = true;
				this.debouncedDoSaveDraft = _debounce(this.doSaveDraft, 1000);
				this.debouncedClearQueryParamsInputValues = _debounce(this.clearQueryParamsInputValues, 1000);
				this.debouncedApplyQueryParams = _debounce(this.applyQueryParams, 100);
			}
		},
		keepDebounceFunctionInFlight () {
			/*
			Resetting this to a fresh _debounce function every time the props to this component changes,
			will keep the last in-flight debounced function call active,
			and won't override it when called for this new RunnitNode.id
			 */
			this.initDebouncedMethods();
		},
		determineFields (node: RunnitNode) {
			return this._get(node, 'nodeDef.fields') || [];
		},
		determineStaticFields (node: RunnitNode) {
			return this._get(node, 'nodeDef.staticFields') || {};
		},
		beginAutoSave (inputValues: Record<string, RunnitNodeRunInputValue>, staticInputValues: Record<RunnitNodeStaticFieldsKey, any>) {
			if (!this.shouldRenderFields) return;
			this.hasChangesToSave = true;
			this.debouncedDoSaveDraft(
				this.draftRunnitNodeRun,
				this.node,
				inputValues,
				staticInputValues,
			);
		},
		async doSaveDraft (
			draftRunnitNodeRun: RunnitNodeRun,
			node: RunnitNode,
			inputValues: Record<string, RunnitNodeRunInputValue>,
			staticInputValues: Record<RunnitNodeStaticFieldsKey, any>,
		) {
			this.hasChangesToSave = false;

			if (!this.allowSave) return;
			if (!node) return;
			if (
				this.isNodeRunQueuing &&
				draftRunnitNodeRun &&
				this.draftRunnitNodeRun &&
				draftRunnitNodeRun.id === this.draftRunnitNodeRun.id
			) return;
			if (_isEmpty(inputValues) && _isEmpty(staticInputValues)) return;
			if (
				draftRunnitNodeRun &&
				(
					draftRunnitNodeRun.state !== RUNNIT_NODE_RUN_STATE.DRAFT || // not draft state
					(
						_isEqual(draftRunnitNodeRun.inputs, inputValues) && // no diff
						_isEqual(draftRunnitNodeRun.staticInputs, staticInputValues) // no diff
					)
				)
			) return;

			try {
				this.isSavingChanges = true;

				// Value mappings
				const mappedInputValues: Record<string, RunnitNodeRunInputValue> = {};
				const mappedStaticInputValues: Record<RunnitNodeStaticFieldsKey, any> = {};
				_keys(inputValues).forEach((key: string) => {
					const v = mapFirebaseTimestampToDate(inputValues[key]);
					// Firestore rejects `undefined` values
					mappedInputValues[key] = v === undefined ? null : v;
				});
				_keys(staticInputValues).forEach((key: RunnitNodeStaticFieldsKey) => {
					const v = mapFirebaseTimestampToDate(staticInputValues[key]);
					// Firestore rejects `undefined` values
					mappedStaticInputValues[key] = v === undefined ? null : v;
				});

				if (draftRunnitNodeRun) {
					await db
						.collection(`runnitNodeRunDrafts`)
						.doc(draftRunnitNodeRun.id)
						.update({
							inputs: mappedInputValues,
							staticInputs: mappedStaticInputValues,
						});
				} else {
					const nowDate: Date = new Date();
					await db
						.collection(`runnitNodeRunDrafts`)
						.add({
							createdAt: nowDate,
							deletedAt: null,
							draftAt: nowDate,
							userId: this.user.id,
							runnitId: node.runnitId,
							nodeId: node.id,
							nodeDefId: node.nodeDefId,
							nodeDef: db.doc(`runnitNodeDefs/${node.nodeDefId}`),
							mode: RUNNIT_NODE_RUN_MODE.MANUAL,
							state: RUNNIT_NODE_RUN_STATE.DRAFT,
							inputs: mappedInputValues,
							staticInputs: mappedStaticInputValues,
						});
				}
			} catch (e) {
				console.error(e);
			} finally {
				this.isSavingChanges = false;
			}
		},
		onFieldInput ({ field, value }: { field: RunnitNodeField, value: any }) {
			if (!field || value === undefined) return;

			const changes: Record<string, RunnitNodeRunInputValue> = {
				...this.inputValues,
				[field.fieldDefUuid]: value,
			};
			this.emitInputValuesChanges(changes);
			this.beginAutoSave(changes, this.staticInputValues);
		},
		emitInputValuesChanges (changes: Record<string, RunnitNodeRunInputValue>) {
			if (_isEmpty(changes)) return;

			this.$emit('on-input-values-input', changes);
		},
		emitStaticInputValuesChanges (changes: Record<RunnitNodeStaticFieldsKey, any>) {
			if (_isEmpty(changes)) return;

			this.$emit('on-static-input-values-input', changes);
		},
		/**
		 * Extract all fieldDefUuids from fields (including nested groups)
		 */
		extractAllFieldDefUuids (fields: (RunnitNodeFieldGroup | RunnitNodeField)[]): Set<string> {
			const uuids = new Set<string>();
			const traverse = (fieldsToTraverse: (RunnitNodeFieldGroup | RunnitNodeField)[]) => {
				(fieldsToTraverse || []).forEach((groupOrField: RunnitNodeFieldGroup | RunnitNodeField) => {
					const group: RunnitNodeFieldGroup = groupOrField as RunnitNodeFieldGroup;
					const field: RunnitNodeField = groupOrField as RunnitNodeField;
					if (group.__rgroup) {
						traverse(group.fields);
					} else if (field.__rfield && field.fieldDefUuid) {
						uuids.add(field.fieldDefUuid);
					}
				});
			};
			traverse(fields);
			return uuids;
		},
		/**
		 * Normalize and heal invalid draft input values based on current field definitions
		 * Returns: { healedInputs, didHeal, healedReasons }
		 */
		normalizeInputValues (
			inputValues: Record<string, RunnitNodeRunInputValue>,
			fields: (RunnitNodeFieldGroup | RunnitNodeField)[]
		): { healedInputs: Record<string, RunnitNodeRunInputValue>, didHeal: boolean, healedReasons: string[] } {
			const healedInputs: Record<string, RunnitNodeRunInputValue> = {};
			const healedReasons: string[] = [];
			let didHeal = false;

			// Step 1: Remove deprecated fields (keys not in current fields)
			const validFieldDefUuids = this.extractAllFieldDefUuids(fields);
			const inputKeys = Object.keys(inputValues);
			const deprecatedKeys = inputKeys.filter(key => !validFieldDefUuids.has(key));
			// if (deprecatedKeys.length > 0) {
			// 	didHeal = true;
			// 	healedReasons.push(`Removed ${deprecatedKeys.length} deprecated field(s)`);
			// }

			// Step 2: Validate and heal each field value
			const validateAndHealField = (field: RunnitNodeField, value: RunnitNodeRunInputValue): RunnitNodeRunInputValue => {
				if (!field || !field.fieldDefUuid) return value;

				const fieldDefUuid: FieldDefUuid = field.fieldDefUuid;
				const currentValue = inputValues[fieldDefUuid];

				// Skip if value is null/undefined (will use default)
				if (_isNil(currentValue)) {
					return currentValue;
				}

				switch (field.type) {
					case RUNNIT_NODE_FIELD_TYPE.WIDTH_HEIGHT: {
						const display = field.display as RunnitNodeFieldDisplayWIDTH_HEIGHT;
						if (!display) return field.defaultValue || null;

						const validationResult = validateWidthHeightValue(display, currentValue);
						if (!validationResult.valid) {
							healedReasons.push(`${field.label + ' - ' || null}${validationResult.reason || 'Invalid resolution option'}`);
							// Healing policy: defaultValue, else null (never return undefined)
							return _isNil(field.defaultValue) ? null : field.defaultValue;
						}
						return currentValue;
					}

					case RUNNIT_NODE_FIELD_TYPE.SINGLE_SELECT: {
						const display = field.display as RunnitNodeFieldDisplaySINGLE_SELECT;
						if (!display) return field.defaultValue || null;

						const validationResult = validateSingleSelectValue(display, currentValue);
						if (!validationResult.valid) {
							healedReasons.push(`${field.label + ' - ' || null}${validationResult.reason || 'Invalid option'}`);
							// Healing policy: defaultValue, else null (never return undefined)
							return _isNil(field.defaultValue) ? null : field.defaultValue;
						}
						return currentValue;
					}

					case RUNNIT_NODE_FIELD_TYPE.SLIDER:
					case RUNNIT_NODE_FIELD_TYPE.CFG:
					case RUNNIT_NODE_FIELD_TYPE.STEPS:
					case RUNNIT_NODE_FIELD_TYPE.UPSCALE_FACTOR_SLIDER: {
						const display = field.display as RunnitNodeFieldDisplaySLIDER | RunnitNodeFieldDisplayCFG | RunnitNodeFieldDisplaySTEPS | RunnitNodeFieldDisplayUPSCALE_FACTOR_SLIDER;
						if (!display) return field.defaultValue || null;

						// Extract numeric value
						let numValue: number | null = null;
						if (_isNumber(currentValue)) {
							numValue = currentValue;
						} else if (typeof currentValue === 'object' && currentValue !== null && _isNumber((currentValue as any).value)) {
							numValue = (currentValue as any).value;
						} else {
							healedReasons.push(`${field.label + ' - ' || null}Invalid numeric value`);
							return field.defaultValue || null;
						}

						if (_isNil(numValue)) return field.defaultValue || null;

						// For UPSCALE_FACTOR_SLIDER with options (no min/max), validate against options directly
						const displayWithOptions = display as any;
						if (field.type === RUNNIT_NODE_FIELD_TYPE.UPSCALE_FACTOR_SLIDER && displayWithOptions.options && displayWithOptions.options.length > 0) {
							const validationResult = validateSliderValue(display, numValue, { requireStepAlignment: true });
							if (!validationResult.valid) {
								healedReasons.push(`${field.label + ' - ' || null}${validationResult.reason || 'Invalid option'}`);
								return field.defaultValue || null;
							}
							return currentValue;
						}

						// For standard sliders, require min/max
						if (_isNil(display.min) || _isNil(display.max)) {
							return currentValue;
						}

						const min: number = display.min;
						const max: number = display.max;

						// Clamp to range first
						if (numValue < min) {
							healedReasons.push(`${field.label + ' - ' || null}Value below minimum`);
							return min;
						}
						if (numValue > max) {
							healedReasons.push(`${field.label + ' - ' || null}Value above maximum`);
							return max;
						}

						// Check step alignment (step defaults to 1 if missing, per user's clarification)
						const validationResult = validateSliderValue(display, numValue, { requireStepAlignment: true });
						if (!validationResult.valid) {
							// Value doesn't align with step, reset to default
							healedReasons.push(`${field.label + ' - ' || null}${validationResult.reason || 'Value doesn\'t align with step'}`);
							return field.defaultValue || null;
						}

						return currentValue;
					}

					default:
						// For other field types, keep value as-is
						return currentValue;
				}
			};

			// Traverse fields and validate/heal each one
			const traverseFields = (fieldsToTraverse: (RunnitNodeFieldGroup | RunnitNodeField)[]) => {
				(fieldsToTraverse || []).forEach((groupOrField: RunnitNodeFieldGroup | RunnitNodeField) => {
					const group: RunnitNodeFieldGroup = groupOrField as RunnitNodeFieldGroup;
					const field: RunnitNodeField = groupOrField as RunnitNodeField;
					if (group.__rgroup) {
						traverseFields(group.fields);
					} else if (field.__rfield && field.fieldDefUuid) {
						const fieldDefUuid: FieldDefUuid = field.fieldDefUuid;
						const originalValue = inputValues[fieldDefUuid];
						const healedValue = validateAndHealField(field, originalValue);

						// Only include if value changed or if it's a valid field
						if (validFieldDefUuids.has(fieldDefUuid)) {
							if (!_isEqual(originalValue, healedValue)) {
								didHeal = true;
							}
							healedInputs[fieldDefUuid] = healedValue;
						}
					}
				});
			};

			traverseFields(fields);
			return { healedInputs, didHeal, healedReasons };
		},
		emitNodeRunDefaults (draftRunnitNodeRun: RunnitNodeRun, node: RunnitNode, clearQueryParams: boolean = false): { inputValues: Record<string, any>, staticInputValues: Record<string, any> } | null {
			if (
				!node ||
				(draftRunnitNodeRun && draftRunnitNodeRun.nodeId !== node.id)
			) {
				return null;
			}

			/*
			Before any other changes or defaults-settings, reset any cross-field triggers from the previous form
			 */
			this.incrementPromptSelectedModelChangeTrigger({ model: null, resetTrigger: true }); // Model selection / injection from Prompt field
			this.updateRunnitState({ generatedNegativePrompt: null }); // Prompt Magic generating negative prompt value from Prompt field

			/*
			Ensure query params are loaded from route if they were cleared from data properties
			This ensures query params persist even if draft loads after they were initially applied
			Note: If nodeRunId needs to be loaded, the caller should use applyQueryParams instead
			 */
			if (!this.inputValuesQueryParam && this.$route.query.inputValues) {
				this.inputValuesQueryParam = this.$route.query.inputValues;
			}
			if (!this.promptQueryParam && this.$route.query.prompt) {
				this.promptQueryParam = this.$route.query.prompt;
			}
			if (!this.nodeRunIdQueryParam && this.$route.query.nodeRunId) {
				this.nodeRunIdQueryParam = this.$route.query.nodeRunId;
				// If nodeRunId exists but values aren't loaded, we can't apply them here
				// The caller should use applyQueryParams instead for async loading
			}

			/*
			Default values from input fields
			 */
			const determinedStaticFields: RunnitNodeStaticFields = this.determineStaticFields(node);
			const promptFieldDefUuids: string[] = [];
			const defaultInputValuesMap: Record<string, RunnitNodeRunInputValue> = {};
			const queryParamInputValuesMap: Record<string, RunnitNodeRunInputValue> = {};
			const extractDefaultValuesFromNodeFields: Function = (fields: (RunnitNodeFieldGroup | RunnitNodeField)[]): void => {
				(fields || []).forEach((groupOrField: RunnitNodeFieldGroup | RunnitNodeField) => {
					const group: RunnitNodeFieldGroup = groupOrField as RunnitNodeFieldGroup;
					const field: RunnitNodeField = groupOrField as RunnitNodeField;
					if (group.__rgroup) {
						extractDefaultValuesFromNodeFields(group.fields);
					} else if (field.__rfield) {
						// Collect default values for all fields
						if (!_isNil(field.defaultValue)) {
							defaultInputValuesMap[field.fieldDefUuid] = field.defaultValue;
						}

						// Track prompt fields for prompt query param handling
						if (field.type === RUNNIT_NODE_FIELD_TYPE.PROMPT) {
							promptFieldDefUuids.push(field.fieldDefUuid);
						}

						// Handle clearing image/video fields when inputValues query param exists
						// but doesn't include this field (for remix scenarios)
						if (this.inputValuesQueryParam) {
							const inputValuesObj = JSON.parse(decodeURIComponent(this.inputValuesQueryParam));
							if (
								!inputValuesObj[field.fieldDefUuid] &&
								(
									field.type === RUNNIT_NODE_FIELD_TYPE.IMG ||
									field.type === RUNNIT_NODE_FIELD_TYPE.IMGS ||
									field.type === RUNNIT_NODE_FIELD_TYPE.VIDEO
								)
							) {
								// Explicitly clear image and video fields that don't exist in remixed generation
								// to prevent tool from reverting to saved state
								queryParamInputValuesMap[field.fieldDefUuid] = null;
							}
						}
					}
				});
			};
			extractDefaultValuesFromNodeFields(this.determinedFields);

			/*
			Apply query params in precedence order (lowest to highest):
			1. nodeRunId (lowest precedence) - applied first
			2. inputValues (middle precedence) - overrides nodeRunId
			3. prompt (highest precedence) - overrides both
			 */

			// Step 1: Apply nodeRunId values (lowest precedence)
			if (this.nodeRunInputValues) {
				Object.keys(this.nodeRunInputValues).forEach((fieldDefUuid) => {
					queryParamInputValuesMap[fieldDefUuid] = this.nodeRunInputValues[fieldDefUuid];
				});
			}

			// Step 2: Apply inputValues (middle precedence - overrides nodeRunId)
			if (this.inputValuesQueryParam) {
				const inputValuesObj = JSON.parse(decodeURIComponent(this.inputValuesQueryParam));
				Object.keys(inputValuesObj).forEach((fieldDefUuid) => {
					queryParamInputValuesMap[fieldDefUuid] = inputValuesObj[fieldDefUuid];
				});
			}

			// Step 3: Apply prompt (highest precedence - overrides both)
			if (this.promptQueryParam && promptFieldDefUuids.length > 0) {
				const firstPromptFieldDefUuid = promptFieldDefUuids[0];
				const promptValue = typeof this.promptQueryParam === 'string' ? this.promptQueryParam : String(this.promptQueryParam);
				queryParamInputValuesMap[firstPromptFieldDefUuid] = {
					value: _trim(promptValue) || null,
					magicMode: this.user?.runnitSettings?.promptMagicDefaultOff
						? PROMPT_MAGIC_MODE.OFF
						: null,
				};
			}

			// Clear query params after processing if requested (they'll be cleared from URL by debouncedClearQueryParamsInputValues)
			// Only clear if draft has loaded to ensure query params persist if draft loads later
			if (clearQueryParams && this.draftRunnitNodeRun) {
				this.inputValuesQueryParam = null;
				this.promptQueryParam = null;
				this.nodeRunInputValues = null;
				this.nodeRunStaticInputValues = null;
				this.nodeRunIdQueryParam = null;
			}

			/*
			Default values from static input fields
			 */
			const defaultStaticInputValuesMap: Record<RunnitNodeStaticFieldsKey | string, any> = {};
			_keys(determinedStaticFields).forEach((key: RunnitNodeStaticFieldsKey) => {
				const staticField: any = determinedStaticFields[key];
				if (!_isNil(staticField.defaultValue)) {
					defaultStaticInputValuesMap[key] = staticField.defaultValue;
				}
			});

			/*
			Emit input values
			 */
			const inputValuesToEmit: Record<string, any> = this.convertUndefinedValueToNull({
				...defaultInputValuesMap,
				...(draftRunnitNodeRun && draftRunnitNodeRun.inputs),
				...queryParamInputValuesMap,
			});
			promptFieldDefUuids.forEach((fieldDefUuid: string) => {
				const promptStringValue: string = _trim(extractPromptStringValue(inputValuesToEmit[fieldDefUuid]));
				inputValuesToEmit[fieldDefUuid] = {
					value: promptStringValue || null,
					magicMode: this.user?.runnitSettings?.promptMagicDefaultOff
						? PROMPT_MAGIC_MODE.OFF
						: (inputValuesToEmit[fieldDefUuid]?.magicMode || null),
				};
			});

			/*
			Normalize and heal invalid draft values
			 */
			const { healedInputs, didHeal, healedReasons } = this.normalizeInputValues(inputValuesToEmit, this.determinedFields);
			const finalInputValuesToEmit = didHeal ? healedInputs : inputValuesToEmit;

			this.emitInputValuesChanges(finalInputValuesToEmit);

			/*
			Emit static values
			 */
			const staticInputValuesToEmit = this.convertUndefinedValueToNull({
				...defaultStaticInputValuesMap,
				...(draftRunnitNodeRun && draftRunnitNodeRun.staticInputs),
				...(this.nodeRunStaticInputValues || {}),
			});
			this.emitStaticInputValuesChanges(staticInputValuesToEmit);

			// Persist healed values back to the draft using the same autosave path as normal field edits.
			// Note: when `clearQueryParams` is true, `applyQueryParams()` will call `beginAutoSave()` using the return value
			// from this method, so avoid double-saving.
			if (didHeal && this.allowSave && !clearQueryParams) {
				const draftId: string | null = draftRunnitNodeRun?.id || null;

				// Only trigger autosave if this is a different draft than we last healed (avoid repeated writes)
				if (!draftId || this.lastHealedDraftId !== draftId) {
					const originalInputs: Record<FieldDefUuid, RunnitNodeRunInputValue> = draftRunnitNodeRun?.inputs || {};
					const originalStaticInputs: Record<RunnitNodeStaticFieldsKey, any> = draftRunnitNodeRun?.staticInputs || {};
					const hasChanges: boolean =
						!_isEqual(originalInputs, finalInputValuesToEmit) ||
						!_isEqual(originalStaticInputs, staticInputValuesToEmit);

					if (hasChanges) {
						if (draftId) {
							this.lastHealedDraftId = draftId;
						}

						// Persist immediately using the same write path as autosave (doSaveDraft),
						// but without relying on the debounce timing (tool switching / fast navigation can cancel the debounce).
						this.doSaveDraft(
							this.draftRunnitNodeRun,
							this.node,
							finalInputValuesToEmit,
							staticInputValuesToEmit,
						);

						// Show snackbar once per page load
						if (!this.hasShownHealSnackbar) {
							this.hasShownHealSnackbar = true;
							const reasonText = healedReasons.length > 0 ? ` Reason: ${healedReasons.join('; ')}` : '';
							this.updateSnackbar({
								status: SNACKBAR_STATUS.ACCENT,
								message: `Some settings were updated to match the latest tool version.${reasonText}`,
								show: true,
								timeout: 10000,
							});
						}
					}
				}
			}

			/*
			Clear query params
			 */
			this.debouncedClearQueryParamsInputValues();

			// Return the values that were emitted so they can be used for autosave
			return {
				inputValues: finalInputValuesToEmit,
				staticInputValues: staticInputValuesToEmit,
			};
		},
		convertUndefinedValueToNull (obj) {
			return Object.keys(obj).reduce((o, key) => {
				o[key] = obj[key] === undefined ? null : obj[key]
				return o;
			}, {})
		},
		clearQueryParamsInputValues () {
			// Note: this method is used debounced since switching between tools can cause the drawer to open and close multiple times
			//	which was causing an issue of clearing the form unless we waited until it was fully initialized.
			const newQuery = { ...this.$route.query };
			delete newQuery.inputValues;
			delete newQuery.prompt;
			delete newQuery.nodeRunId;
			this.routerReplace(this.$route, this.$router, { query: newQuery });
		},
		async loadNodeRunInputValues (nodeRunId: string) {
			try {
				const nodeRunRef = db.collection('runnitNodeRuns').doc(nodeRunId);
				const nodeRunDoc = await nodeRunRef.get();

				if (!nodeRunDoc.exists) {
					console.error('NodeRun not found:', nodeRunId);
					this.nodeRunInputValues = null;
					this.nodeRunStaticInputValues = null;
					return;
				}

				const nodeRun: RunnitNodeRun = {
					...nodeRunDoc.data(),
					get id () { return nodeRunDoc.id; },
				} as RunnitNodeRun;

				// Extract input values using the mixin method
				const inputValues = await this.fetchNodeRunValues(nodeRun, nodeRunId);
				this.nodeRunInputValues = inputValues || null;

				// Also store static input values
				this.nodeRunStaticInputValues = nodeRun.staticInputs || null;
			} catch (error) {
				console.error('Error loading nodeRun input values:', error);
				this.nodeRunInputValues = null;
				this.nodeRunStaticInputValues = null;
			}
		},
		async applyQueryParams () {
			if (!this.shouldRenderFields || !this.node) return;

			// Capture the current nodeRunId to ensure we don't apply stale data
			const currentNodeRunId = this.nodeRunIdQueryParam;

			// If nodeRunId is present, we need to load it first (async operation)
			if (currentNodeRunId) {
				this.isLoadingQueryParams = true;
				try {
					await this.loadNodeRunInputValues(currentNodeRunId);
					// Verify nodeRunId hasn't changed during the async load
					if (this.nodeRunIdQueryParam !== currentNodeRunId) {
						// nodeRunId changed during load, abort this application
						return;
					}
				} catch (error) {
					console.error('Error loading nodeRun for query params:', error);
					// If load failed and nodeRunId changed, abort
					if (this.nodeRunIdQueryParam !== currentNodeRunId) {
						return;
					}
				} finally {
					this.isLoadingQueryParams = false;
				}
			} else {
				// Clear nodeRun values if nodeRunId is not present
				this.nodeRunInputValues = null;
				this.nodeRunStaticInputValues = null;
			}

			// Verify node hasn't changed during async operations
			if (!this.node || !this.shouldRenderFields) return;

			// Now apply all query params with correct precedence
			// Precedence order: nodeRunId (lowest) -> inputValues (middle) -> prompt (highest)
			// Pass clearQueryParams=true since we're processing query params here
			const result = this.emitNodeRunDefaults(this.draftRunnitNodeRun, this.node, true);
			if (result) {
				this.beginAutoSave(result.inputValues, result.staticInputValues);
			}
		},
		async initModelDraft () {
			const selectedNodeId = this.runnitState.selectedNodeId ? this.runnitState.selectedNodeId : this.runnitNodes[0]?.id;
			const nodeDef: RunnitNodeDef = (this.runnitNodesMap && selectedNodeId) ? this.runnitNodesMap[selectedNodeId]?.nodeDef : null;

			if (nodeDef?.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER && !this.modelsState.modelDraft) {
				const trainingTypeTag = Object.keys(nodeDef.tags).find((tagId: string) => this.publicRunnitNodeDefTagsMap[tagId]?.type === RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_TYPE);
				const trainingQualityTag = Object.keys(nodeDef.tags).find((tagId: string) => this.publicRunnitNodeDefTagsMap[tagId]?.type === RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_QUALITY);

				const modelDraft = await this.createModelDraft();
				await this.updateModelState({
					modelDraft: {
						...modelDraft,
						tagTypes: {
							...(modelDraft.tagTypes || {}),
							[RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_TYPE]: true,
							[RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_QUALITY]: true,
						},
						tags: {
							...(modelDraft.tags || {}),
							[trainingTypeTag]: true,
							[trainingQualityTag]: true,
						},
					},
				});
			}
		},
		buildFieldsByTypeMap (fields: (RunnitNodeFieldGroup | RunnitNodeField)[]) {
			const fieldsByType: Record<RunnitNodeFieldType, RunnitNodeField[]> = {} as Record<RunnitNodeFieldType, RunnitNodeField[]>;

			// Initialize all field types to false
			Object.keys(RUNNIT_NODE_FIELD_TYPE).forEach(key => {
				fieldsByType[RUNNIT_NODE_FIELD_TYPE[key]] = [];
			});

			// Recursively traverse fields and mark found types as true
			const traverseFields = (fieldsToTraverse: (RunnitNodeFieldGroup | RunnitNodeField)[]) => {
				(fieldsToTraverse || []).forEach(groupOrField => {
					const group: RunnitNodeFieldGroup = groupOrField as RunnitNodeFieldGroup;
					const field: RunnitNodeField = groupOrField as RunnitNodeField;

					if (group.__rgroup) {
						traverseFields(group.fields);
					} else if (field.__rfield) {
						if (field.type) {
							fieldsByType[field.type] = fieldsByType[field.type] || [];
							fieldsByType[field.type].push(field);
						}
					}
				});
			};
			traverseFields(fields);

			// Update the state with the new field types
			this.updateRunnitState({
				fieldsByType,
			});
		},
		initPromptDrivenFormFieldGrouping () {
			let primaryFormPromptField: RunnitNodeField = null;
			const chipFields: RunnitNodeField[] = [];
			const mainActionFields: RunnitNodeField[] = [];
			const mainFields: RunnitNodeField[] = [];
			const advancedFields: (RunnitNodeField | RunnitNodeFieldGroup)[] = [];

			const aggregateFieldsByPrimaryForm = (fields: (RunnitNodeField | RunnitNodeFieldGroup)[], parentGroup: RunnitNodeFieldGroup | null) => {
				(fields || []).forEach((groupOrField: RunnitNodeField | RunnitNodeFieldGroup) => {
					const group: RunnitNodeFieldGroup = groupOrField as RunnitNodeFieldGroup;
					const field: RunnitNodeField = groupOrField as RunnitNodeField;
					if (group.__rgroup) {
						// Add the group itself to advancedFields to maintain structure
						const newGroup: RunnitNodeFieldGroup = {
							...group,
							fields: [] // Initialize empty fields array that we'll populate
						};
						if (parentGroup) {
							parentGroup.fields.push(newGroup);
						} else {
							advancedFields.push(newGroup);
						}
						aggregateFieldsByPrimaryForm(group.fields, newGroup);
					} else if (field.__rfield) {
						switch (field.display?.primaryForm) {
							case 'PROMPT':
								primaryFormPromptField = field;
								mainFields.push(field);
								break;
							case 'CHIP':
								// chipFields is PrimaryForm-only, so it stays gated on whether
								// DynamicChipField can actually draw the type. Without that
								// guard an unsupported type vanishes from PrimaryForm: pulled
								// out of advancedFields, then rendered as an empty div.
								if (hasChipRenderer(field.type)) {
									chipFields.push(field);
									mainFields.push(field);
									break;
								}
								// Not chip-renderable. It still belongs in the DRAWER's main
								// section rather than its Advanced panel: the drawer renders
								// mainFields with RunnitDynamicField, which draws every type.
								// It stays in advancedFields too, because PrimaryForm has no
								// other place to put it, and filterRenderableAdvancedFields
								// drops anything with a primaryForm from the drawer's Advanced
								// panel so it is not rendered twice. Same shape as
								// MODEL_SINGLE_SELECT below.
								mainFields.push(field);
								if (parentGroup) {
									parentGroup.fields.push(field);
								} else {
									advancedFields.push(field);
								}
								break;
							case 'MAIN_ACTION':
								mainActionFields.push(field);
								mainFields.push(field);
								break;
							default:
								if (field.type === RUNNIT_NODE_FIELD_TYPE.MODEL_SINGLE_SELECT) {
									mainFields.push(field);
								}
								if (parentGroup) {
									parentGroup.fields.push(field);
								} else {
									advancedFields.push(field);
								}
								break;
						}
					}
				});
			};

			aggregateFieldsByPrimaryForm(this.determinedFields, null);

			// Remove any groups that have no fields
			const filteredAdvancedFields = advancedFields.filter(field => {
				if ('__rgroup' in field && field.__rgroup) {
					return (field as RunnitNodeFieldGroup).fields.length > 0;
				}
				return true;
			});

			this.primaryFormPromptField = primaryFormPromptField;
			this.chipFields = chipFields;
			this.mainActionFields = mainActionFields;
			this.mainFields = mainFields;
			this.advancedFields = filteredAdvancedFields;
		},
		onPromptLoadingChanged (isLoading: boolean) {
			this.$emit('loading-changed', isLoading);
		},
		/**
		 * Traverse determinedFields in order and return the first PROMPT, first IMG/IMGS, and first VIDEO field.
		 */
		getFirstFieldsForPaste (): FirstFieldsForPaste {
			let firstImageField: RunnitNodeField | null = null;
			let firstVideoField: RunnitNodeField | null = null;

			const traverse = (fields: (RunnitNodeFieldGroup | RunnitNodeField)[]) => {
				for (const groupOrField of fields || []) {
					const group = groupOrField as RunnitNodeFieldGroup;
					const field = groupOrField as RunnitNodeField;
					if (group.__rgroup) {
						traverse(group.fields);
					} else if (field.__rfield) {
						if ((field.type === RUNNIT_NODE_FIELD_TYPE.IMG || field.type === RUNNIT_NODE_FIELD_TYPE.IMGS) && !firstImageField) {
							firstImageField = field;
						}
						if (field.type === RUNNIT_NODE_FIELD_TYPE.VIDEO && !firstVideoField) {
							firstVideoField = field;
						}
					}
					if (firstImageField && firstVideoField) return;
				}
			};
			traverse(this.determinedFields);
			return { firstImageField, firstVideoField };
		},
		async onPaste (ev: ClipboardEvent) {
			const clipboardData = ev?.clipboardData;
			if (!clipboardData) return;

			const { firstImageField, firstVideoField } = this.getFirstFieldsForPaste();

			const items: DataTransferItem[] = Array.from(clipboardData.items);
			const imageItems = items.filter((item: DataTransferItem) => item.type.startsWith('image/'));
			const imageFiles: File[] = imageItems
				.map((item: DataTransferItem) => item.getAsFile())
				.filter((file: File | null): file is File => file != null);
			const videoItem = items.find((item: DataTransferItem) => item.type.startsWith('video/'));

			if (imageFiles.length > 0 && firstImageField) {
				ev.preventDefault();
				ev.stopPropagation();
				this.isPasteUploading = true;
				this.pasteUploadingCount = imageFiles.length;
				try {
					const maxImages: number | null | undefined = this._get(firstImageField, 'display.maxImages');
					const currentValue = this.inputValues[firstImageField.fieldDefUuid] as MultiImageInputValue | undefined;
					const currentUploads: RunnitUpload[] = currentValue?.uploads ?? [];
					const currentNodeRunResults: RunnitNodeRunResult[] = currentValue?.nodeRunResults ?? [];
					const currentCount = currentUploads.length + currentNodeRunResults.length;
					const isMulti = firstImageField.type === RUNNIT_NODE_FIELD_TYPE.IMGS;

					if (isMulti) {
						// Multi-image: add as many pasted images as we can, respecting maxImages
						const availableSlots = maxImages != null ? Math.max(0, maxImages - currentCount) : imageFiles.length;
						if (availableSlots <= 0) {
							this.updateSnackbar({
								status: SNACKBAR_STATUS.ERROR,
								message: 'Too many images already selected',
								show: true,
							});
							return;
						}
						const filesToUpload = imageFiles.slice(0, availableSlots);
						const newUploads: RunnitUpload[] = [];
						for (const file of filesToUpload) {
							const upload = await uploadImageToRunnit(file);
							if (upload) newUploads.push({ ...upload, id: upload.id } as RunnitUpload);
						}
						if (newUploads.length > 0) {
							const value: RunnitNodeRunInputValue = {
								...(currentValue || {}),
								uploads: [...currentUploads, ...newUploads],
							};
							this.onFieldInput({ field: firstImageField, value });
							this.beginAutoSave({ ...this.inputValues, [firstImageField.fieldDefUuid]: value }, this.staticInputValues);
							const skipped = imageFiles.length - filesToUpload.length;
							if (skipped > 0) {
								this.updateSnackbar({
									status: SNACKBAR_STATUS.ACCENT,
									message: `Only ${filesToUpload.length} of ${imageFiles.length} pasted images could be added (max ${maxImages})`,
									show: true,
								});
							}
						}
					} else {
						// Single image: use only the first pasted image
						const upload = await uploadImageToRunnit(imageFiles[0]);
						if (upload) {
							// Normalize so id is an enumerable property; otherwise Vuex/reactivity can lose the getter and ImageInput won't see value.upload.id
							const uploadPlain = { ...upload, id: upload.id };
							const current = this.inputValues[firstImageField.fieldDefUuid] as Record<string, unknown> | undefined;
							const hasStrength = !!this._get(firstImageField, 'display.hasStrength');
							const hasInpainting = !!this._get(firstImageField, 'display.hasInpainting');
							let value: RunnitNodeRunInputValue;
							if (hasStrength || hasInpainting) {
								// Image to Image (and similar) expect { image, inpainting?, strength? }
								value = {
									image: { upload: uploadPlain },
									...(hasInpainting && { inpainting: this._get(current, 'inpainting') || { mask: null } }),
									...(hasStrength && { strength: this._get(current, 'strength') ?? 0.8 }),
								} as RunnitNodeRunInputValue;
							} else {
								value = { upload: uploadPlain };
							}
							this.onFieldInput({ field: firstImageField, value });
							this.beginAutoSave({ ...this.inputValues, [firstImageField.fieldDefUuid]: value }, this.staticInputValues);
						}
					}
				} catch (e: unknown) {
					console.error(e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Failed to upload pasted image',
						show: true,
					});
				} finally {
					this.isPasteUploading = false;
					this.pasteUploadingCount = 0;
				}
				return;
			}

			if (videoItem && firstVideoField) {
				const file: File | null = videoItem.getAsFile();
				if (file) {
					ev.preventDefault();
					ev.stopPropagation();
					this.isPasteUploading = true;
					this.pasteUploadingCount = 1;
					try {
						const upload = await uploadVideoToRunnit(file, this.user.id);
						if (upload) {
							const uploadPlain = { ...upload, id: upload.id };
							this.onFieldInput({ field: firstVideoField, value: { upload: uploadPlain } });
							this.beginAutoSave({ ...this.inputValues, [firstVideoField.fieldDefUuid]: { upload: uploadPlain } }, this.staticInputValues);
						}
					} catch (e: unknown) {
						console.error(e);
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: 'Failed to upload pasted video',
							show: true,
						});
					} finally {
						this.isPasteUploading = false;
						this.pasteUploadingCount = 0;
					}
				}
				return;
			}
		},
	},
	components: {
		RunnitDynamicField,
		LoadingState,
	},
});
